import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AdminHomepage from './pages/AdminHomepage';
import UsersManagement from './pages/UsersManagement';
import UserDetail from './pages/UserDetail';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import PaymentGatewayPage from './pages/PaymentGatewayPage';
import SubscriptionPlansAdminPage from './pages/SubscriptionPlansAdminPage';
import ProgressionAdminPage from './pages/ProgressionAdminPage';
import JobsAdminPage from './pages/JobsAdminPage';
import EmailsAdminPage from './pages/EmailsAdminPage';
import GlobalDashboardPage from './pages/GlobalDashboardPage';
import BehaviorDashboardPage from './pages/BehaviorDashboardPage';
import FinanceDashboardPage from './pages/FinanceDashboardPage';
import TeachersDashboardPage from './pages/TeachersDashboardPage';
import TeacherDetailPage from './pages/TeacherDetailPage';
import AlertsDashboardPage from './pages/AlertsDashboardPage';
import MessageModerationPage from './pages/MessageModerationPage';
import CourseSessionsAdminPage from './pages/CourseSessionsAdminPage';
import FilesAdminPage from './pages/FilesAdminPage';
import PlaybacksAdminPage from './pages/PlaybacksAdminPage';
import PlaybackEditorEmbed from './pages/PlaybackEditorEmbed';
import ExercisesAdminPage from './pages/ExercisesAdminPage';
import PageEditorAdminPage from './pages/PageEditorAdminPage';
import PageEditorEmbed from './pages/PageEditorEmbed';
import FormationsListPage from './pages/FormationsListPage';
import FormationEditorPage from './pages/FormationEditorPage';
import LevelEditorPage from './pages/LevelEditorPage';
import LessonEditorPage from './pages/LessonEditorPage';
import LanguageRouteWrapper from './components/Layouts/LanguageRouteWrapper';
import AdminDashboardLayout from './components/Layouts/AdminDashboardLayout';
import AdminRouteGuard from './components/AdminRouteGuard';
import SuperAdminRouteGuard from './components/SuperAdminRouteGuard';
import { AdminScopeProvider, useAdminScope } from './context/AdminScopeContext';
import { DEFAULT_UI_LANGUAGE } from './i18n/language';
import { logAdmin } from './utils/adminDebug';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string) || '';
const LOGIN_ORIGIN = (import.meta.env.VITE_LOGIN_ORIGIN as string) || 'https://login.themusic.school';
const APP_ORIGIN = (import.meta.env.VITE_APP_ORIGIN as string) || 'https://app.themusic.school';

const getPreferredLanguage = () => DEFAULT_UI_LANGUAGE;

const RouteLogger: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    logAdmin('route', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    });
  }, [location]);
  return null;
};

const GuardedShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<'checking' | 'ok' | 'redirecting'>('checking');
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);

  const meUrl = useMemo(() => `${API_BASE_URL}/api/v1/auth/me`, []);
  const adminPingUrl = useMemo(() => `${API_BASE_URL}/api/v1/admin/ping`, []);

  useEffect(() => {
    logAdmin('boot', {
      apiBaseUrl: API_BASE_URL || '(empty — set VITE_API_BASE_URL)',
      loginOrigin: LOGIN_ORIGIN,
      appOrigin: APP_ORIGIN,
      href: window.location.href,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        logAdmin('auth check start', { meUrl });
        const meRes = await fetch(meUrl, { credentials: 'include' });
        const me = await meRes.json().catch(() => ({}));
        const role = me?.data?.role;
        logAdmin('auth /me response', {
          ok: meRes.ok,
          status: meRes.status,
          success: me?.success,
          role,
        });

        if (!meRes.ok || !me?.success || !role) {
          logAdmin('redirect → login (no session)', { loginOrigin: LOGIN_ORIGIN });
          if (!cancelled) setStatus('redirecting');
          window.location.assign(`${LOGIN_ORIGIN}/?surface=admin&returnTo=/`);
          return;
        }
        if (role !== 'Admin') {
          logAdmin('redirect → app (not admin)', { role, appOrigin: APP_ORIGIN });
          if (!cancelled) setStatus('redirecting');
          window.location.assign(`${APP_ORIGIN}/`);
          return;
        }

        logAdmin('auth admin ping start', { adminPingUrl });
        const pingRes = await fetch(adminPingUrl, { credentials: 'include' });
        const pingData = await pingRes.json().catch(() => ({}));
        logAdmin('auth /admin/ping response', {
          ok: pingRes.ok,
          status: pingRes.status,
          pingData,
        });

        if (!pingRes.ok) {
          const err: any = new Error(pingData?.message || `HTTP ${pingRes.status}`);
          err.status = pingRes.status;
          throw err;
        }
        if (!cancelled) {
          setStatus('ok');
          logAdmin('auth guard passed');
        }
      } catch (e: any) {
        logAdmin('auth guard error', { message: e?.message, status: e?.status });
        const code = e?.status;
        if (code === 401) {
          if (!cancelled) setStatus('redirecting');
          window.location.assign(`${LOGIN_ORIGIN}/?surface=admin&returnTo=/`);
          return;
        }
        if (code === 403) {
          if (!cancelled) setStatus('redirecting');
          window.location.assign(`${APP_ORIGIN}/`);
          return;
        }
        if (!cancelled) {
          setError(e?.message || 'Failed to verify session');
          setDebugInfo(`meUrl=${meUrl} pingUrl=${adminPingUrl}`);
          setStatus('ok');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meUrl, adminPingUrl]);

  if (status === 'checking') {
    return (
      <Box minHeight="100vh" display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1}>
        <CircularProgress />
        <Typography variant="caption" color="text.secondary">
          Verifying admin session…
        </Typography>
      </Box>
    );
  }

  if (status === 'redirecting') {
    return (
      <Box minHeight="100vh" display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1}>
        <CircularProgress />
        <Typography variant="caption" color="text.secondary">
          Redirecting…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" px={2}>
        <Box textAlign="center">
          <Typography color="error" gutterBottom>
            {error}
          </Typography>
          {debugInfo ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {debugInfo}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
  }

  return <>{children}</>;
};

const AdminIndexRedirect: React.FC = () => {
  const { homePath, loading } = useAdminScope();
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  const segment = homePath.replace(/^\//, '');
  return <Navigate to={segment} replace />;
};

const App: React.FC = () => {
  useEffect(() => {
    logAdmin('App mounted');
  }, []);

  return (
    <GuardedShell>
      <AdminScopeProvider>
        <RouteLogger />
        <Routes>
          <Route path="/" element={<Navigate to={`/${getPreferredLanguage()}`} replace />} />

          <Route path="/:lang" element={<LanguageRouteWrapper />}>
            <Route element={<AdminDashboardLayout />}>
              <Route index element={<AdminIndexRedirect />} />
              <Route path="home" element={<AdminRouteGuard><AdminHomepage /></AdminRouteGuard>} />
              <Route path="dashboard" element={<AdminRouteGuard><GlobalDashboardPage /></AdminRouteGuard>} />
              <Route path="alerts" element={<AdminRouteGuard><AlertsDashboardPage /></AdminRouteGuard>} />
              <Route path="moderation" element={<AdminRouteGuard><MessageModerationPage /></AdminRouteGuard>} />
              <Route path="sessions" element={<AdminRouteGuard><CourseSessionsAdminPage /></AdminRouteGuard>} />
              <Route path="users" element={<AdminRouteGuard><UsersManagement /></AdminRouteGuard>} />
              <Route path="users/:userId" element={<AdminRouteGuard><UserDetail /></AdminRouteGuard>} />
              <Route path="behavior" element={<AdminRouteGuard><BehaviorDashboardPage /></AdminRouteGuard>} />
              <Route path="finance" element={<AdminRouteGuard><FinanceDashboardPage /></AdminRouteGuard>} />
              <Route path="teachers" element={<AdminRouteGuard><TeachersDashboardPage /></AdminRouteGuard>} />
              <Route path="teachers/:teacherId" element={<AdminRouteGuard><TeacherDetailPage /></AdminRouteGuard>} />
              <Route path="analytics/:userId" element={<AdminRouteGuard><AnalyticsDashboard /></AdminRouteGuard>} />
              <Route path="page-editor" element={<AdminRouteGuard><PageEditorEmbed /></AdminRouteGuard>} />
              <Route path="page-editor/documents" element={<AdminRouteGuard><PageEditorAdminPage /></AdminRouteGuard>} />
              <Route path="files" element={<AdminRouteGuard><FilesAdminPage /></AdminRouteGuard>} />
              <Route path="playbacks" element={<AdminRouteGuard><PlaybacksAdminPage /></AdminRouteGuard>} />
              <Route path="playbacks/new" element={<AdminRouteGuard><PlaybackEditorEmbed /></AdminRouteGuard>} />
              <Route path="playbacks/:playbackId" element={<AdminRouteGuard><PlaybackEditorEmbed /></AdminRouteGuard>} />
              <Route path="exercises" element={<AdminRouteGuard><ExercisesAdminPage /></AdminRouteGuard>} />
              <Route path="formations" element={<AdminRouteGuard><FormationsListPage /></AdminRouteGuard>} />
              <Route path="formations/:formationId" element={<AdminRouteGuard><FormationEditorPage /></AdminRouteGuard>} />
              <Route path="formations/:formationId/levels/:levelId" element={<AdminRouteGuard><LevelEditorPage /></AdminRouteGuard>} />
              <Route
                path="formations/:formationId/levels/:levelId/lessons/:lessonId"
                element={<AdminRouteGuard><LessonEditorPage /></AdminRouteGuard>}
              />
              <Route path="progression" element={<AdminRouteGuard><ProgressionAdminPage /></AdminRouteGuard>} />
              <Route path="jobs" element={<AdminRouteGuard><JobsAdminPage /></AdminRouteGuard>} />
              <Route path="emails" element={<AdminRouteGuard><SuperAdminRouteGuard><EmailsAdminPage /></SuperAdminRouteGuard></AdminRouteGuard>} />
              <Route path="payment-gateway" element={<AdminRouteGuard><PaymentGatewayPage /></AdminRouteGuard>} />
              <Route path="subscription-plans" element={<AdminRouteGuard><SuperAdminRouteGuard><SubscriptionPlansAdminPage /></SuperAdminRouteGuard></AdminRouteGuard>} />
              <Route path="*" element={<AdminIndexRedirect />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to={`/${getPreferredLanguage()}`} replace />} />
        </Routes>
      </AdminScopeProvider>
    </GuardedShell>
  );
};

export default App;
